import React, { useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Toast } from "primereact/toast";
import { Panel } from "primereact/panel";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";

import YearDropdown from "../../components/inputs/YearDropdown";
import useDatevExports from "../../hooks/useDatevExports";
import DatevExportCell from "./datevExports/DatevExportCell";
import {
  DatevAccountOverrides,
  DatevExportRow,
  DatevKind,
  DatevSource,
  MONTH_NAMES,
  slotKey,
} from "./datevExports/datevExportTypes";

const SOURCES: { source: DatevSource; kind: DatevKind }[] = [
  { source: "direct-payment", kind: "revenues" },
  { source: "direct-payment", kind: "payments" },
  { source: "direct-payment", kind: "receipts" },
  { source: "payment", kind: "revenues" },
  { source: "payment", kind: "payments" },
  { source: "payment", kind: "receipts" },
];

const DEFAULT_OVERRIDES: DatevAccountOverrides = {
  debtorAccount: "10010", revenueAccount: "8400", bankAccount: "1211", stripeFeesAccount: "4970",
};
// Bump the key whenever DEFAULT_OVERRIDES changes: loadOverrides merges cached-over-default,
// so stale per-browser values would otherwise keep winning over the new defaults.
const OVERRIDES_KEY = "datev-export-overrides-v3";

const loadOverrides = (): DatevAccountOverrides => {
  try {
    const raw = localStorage.getItem(OVERRIDES_KEY);
    return raw ? { ...DEFAULT_OVERRIDES, ...JSON.parse(raw) } : { ...DEFAULT_OVERRIDES };
  } catch {
    return { ...DEFAULT_OVERRIDES };
  }
};

const DatevExportsPage: React.FC = () => {
  const toastRef = useRef<Toast>(null);

  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState<number>(now.getFullYear());
  const [overrides, setOverrides] = useState<DatevAccountOverrides>(loadOverrides);

  useEffect(() => {
    try { localStorage.setItem(OVERRIDES_KEY, JSON.stringify(overrides)); } catch { /* ignore */ }
  }, [overrides]);

  const { rows, isBusy, anyGenerating, generate, download } = useDatevExports({ year, overrides, toastRef });

  const rowMap = useMemo(() => {
    const m = new Map<string, DatevExportRow>();
    rows.forEach((r) => m.set(slotKey(r.month, r.source, r.kind), r));
    return m;
  }, [rows]);

  const isFuture = (month: number) =>
    year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth() + 1);

  const cellBody = (source: DatevSource, kind: DatevKind) => (rowData: { month: number }) => {
    const month = rowData.month;
    const key = slotKey(month, source, kind);
    const row = rowMap.get(key);
    return (
      <DatevExportCell
        row={row}
        disabled={isFuture(month)}
        busy={isBusy(key)}
        locked={anyGenerating}
        onGenerate={() => generate(month, source, kind)}
        onDownload={() => row && download(row)}
      />
    );
  };

  const headerGroup = (
    <ColumnGroup>
      <Row>
        <Column header="Month" rowSpan={2} />
        <Column header="Direct Payment" colSpan={3} className="text-center" />
        <Column header="Payment" colSpan={3} className="text-center" />
      </Row>
      <Row>
        <Column header="Revenues" />
        <Column header="Payments" />
        <Column header="Receipts" />
        <Column header="Revenues" />
        <Column header="Payments" />
        <Column header="Receipts" />
      </Row>
    </ColumnGroup>
  );

  const months = useMemo(() => MONTH_NAMES.map((_, i) => ({ month: i + 1 })), []);
  const updateOverride = (k: keyof DatevAccountOverrides, v: string) => setOverrides((p) => ({ ...p, [k]: v }));

  return (
    <div className="surface-section p-4">
      <Toast ref={toastRef} />
      <div className="flex align-items-center justify-content-between mb-4 gap-3">
        <div>
          <h2 className="m-0">DATEV Exports</h2>
          <p className="text-color-secondary mt-2 mb-0">
            Generate and download DATEV EXTF files per month. Receipt ZIPs build in the background.
          </p>
        </div>
        <YearDropdown year={year} onChange={(o) => o && setYear(o.code as number)} yearsOffset={5} />
      </div>

      <Panel header="Advanced — SKR03 account overrides" toggleable collapsed className="mb-4">
        <div className="grid">
          {([
            ["debtorAccount", "Debtor (10010)"],
            ["revenueAccount", "Revenue (8400)"],
            ["bankAccount", "Bank (1211)"],
            ["stripeFeesAccount", "Stripe fees (4970)"],
          ] as [keyof DatevAccountOverrides, string][]).map(([k, label]) => (
            <div className="col-12 md:col-6" key={k}>
              <label className="block mb-2 text-sm" htmlFor={k}>{label}</label>
              <InputText id={k} value={overrides[k]} onChange={(e) => updateOverride(k, e.target.value)} className="w-full" />
            </div>
          ))}
        </div>
        <div className="flex justify-content-end mt-3">
          <Button label="Reset to defaults" icon="pi pi-refresh" text onClick={() => setOverrides({ ...DEFAULT_OVERRIDES })} />
        </div>
      </Panel>

      <DataTable value={months} headerColumnGroup={headerGroup} dataKey="month" className="p-datatable-gridlines">
        <Column body={(r: { month: number }) => MONTH_NAMES[r.month - 1]} />
        {SOURCES.map(({ source, kind }) => (
          <Column key={slotKey(0, source, kind)} body={cellBody(source, kind)} className="text-center" />
        ))}
      </DataTable>
    </div>
  );
};

export default DatevExportsPage;
